import {
  useContext,
  createContext,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";

import PropTypes from "prop-types";

import useUser from "@/users/useUser";
import addReadPageIdentifier from "@/view/ProseMirrorNode/addAdditionalInfoToProseMirrorNode/utils/addReadPageIdentifier";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import getArticleAndAnnotationsData from "@/view/utils/getArticleAndAnnotationsData";
import useAnnotationFocusHandling from "@/view/utils/useAnnotationFocusHandling";
import useLazyAnnotationsGet from "@/view/utils/useLazyAnnotationsGet";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import { proseMirrorNodePropType } from "@/view/viewPropTypes";
import useLegacyArticleHeaderGet from "../useLegacyArticleHeaderGet";
import getAnnotationsToShow from "../getAnnotationsToShow";

const AnnotationContext = createContext();

const propTypes = {
  children: PropTypes.node,
  legacyArticleSlug: PropTypes.string,
  levelId: PropTypes.string,
  rawText: proseMirrorNodePropType,
};

export const AnnotationContextProvider = ({
  children,
  legacyArticleSlug,
  levelId,
  rawText,
}) => {
  const { data: user } = useUser();

  const [annotationHighlightsState, setAnnotationHighlightsState] =
    useState(null);
  const [draftAnnotation, setDraftAnnotation] = useState(null);
  const [newlySavedAnnotationId, setNewlySavedAnnotationId] = useState(null);
  const annotationCardRefs = useRef({});
  const contentBodyAnnotationRefs = useRef({});
  const { isReadAloudModeActive } = useReadAloudMode();
  const { isStudentPreviewOn } = useStudentPreviewContext();

  const [getAnnotations, { data: allAnnotations }] =
    useLazyAnnotationsGet(levelId);
  const annotationsToShow = useMemo(() => {
    return getAnnotationsToShow(user, allAnnotations, isStudentPreviewOn);
  }, [allAnnotations, isStudentPreviewOn]);
  const annotations = [];
  const annotationComments = [];

  annotationsToShow?.forEach((annotation) => {
    if (annotation.in_response_to) {
      annotationComments.push(annotation);
    } else {
      annotations.push(annotation);
    }
  });

  useEffect(() => {
    getAnnotations();
  }, [levelId]);

  const annotationsIncludingPossibleDraftAnnotation = draftAnnotation
    ? annotations?.concat(draftAnnotation)
    : annotations;

  const {
    highlightedText,
    orderedListOfAnnotations,
    ...contentDataWithAnnotations
  } = useMemo(
    () =>
      getArticleAndAnnotationsData({
        annotations: annotationsIncludingPossibleDraftAnnotation,
        rawText,
        removePowerWords: isReadAloudModeActive,
      }),
    [
      JSON.stringify(rawText),
      JSON.stringify(annotationsIncludingPossibleDraftAnnotation),
      isReadAloudModeActive,
    ],
  );

  const { focusAnnotationCard, focusContentBodyAnnotation } =
    useAnnotationFocusHandling({
      annotationCardRefs,
      contentBodyAnnotationRefs,
    });

  const hasNoAnnotations = !annotations.length && !draftAnnotation;

  const { data: legacyArticleHeaderData } =
    useLegacyArticleHeaderGet(legacyArticleSlug);
  const updateAnnotationForReadPage = (annotation) => {
    const legacyArticleTextContainsHTML =
      legacyArticleHeaderData?.articles[0]?.text?.includes("<p>");
    if (legacyArticleTextContainsHTML) {
      return addReadPageIdentifier(annotation, rawText);
    }
    return annotation;
  };

  return (
    <AnnotationContext.Provider
      value={{
        annotations,
        annotationCardRefs,
        annotationComments,
        annotationHighlightsState,
        annotationsIncludingPossibleDraftAnnotation,
        contentBodyAnnotationRefs,
        contentDataWithAnnotations,
        draftAnnotation,
        focusAnnotationCard,
        focusContentBodyAnnotation,
        getAnnotations,
        hasNoAnnotations,
        highlightedText,
        newlySavedAnnotationId,
        orderedListOfAnnotations,
        setAnnotationHighlightsState,
        setDraftAnnotation,
        setNewlySavedAnnotationId,
        updateAnnotationForReadPage,
      }}
    >
      {children}
    </AnnotationContext.Provider>
  );
};

AnnotationContextProvider.propTypes = propTypes;

const useAnnotationsState = () => {
  const annotationsContext = useContext(AnnotationContext);

  if (annotationsContext === undefined) {
    throw new Error(
      "useAnnotationsState must be used within a AnnotationContextProvider.",
    );
  }

  return annotationsContext;
};

export default useAnnotationsState;
